import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, Output, TemplateRef, ViewChild, inject } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { CerrixButtonComponent } from "@app/shared/atoms/cerrix-button/cerrix-button.component";
import { CerrixInputComponent } from "@app/shared/atoms/cerrix-input/cerrix-input.component";
import { CerrixInstructionsIconComponent } from "@app/shared/cerrix-instructions-icon/cerrix-instructions-icon.component";
import { SharedModule } from "@app/shared/shared.module";
import { HolidayListModel, HolidayModel } from "@app/workday-organizer/models/holiday-model";

@Component({
    selector: "holidays-daysoff",
    imports: [
        CommonModule,
        SharedModule,
        CerrixInstructionsIconComponent,
        CerrixButtonComponent,
        CerrixInputComponent,
    ],
    templateUrl: "./holidays-daysoff.component.html",
    styleUrl: "./holidays-daysoff.component.scss"
})
export class HolidaysDaysoffComponent {
    private dialog = inject(MatDialog);

    private _holidaysDaysOff: HolidayModel[] = [];
    public get holidaysDaysOff(): HolidayModel[] {
        return this._holidaysDaysOff;
    }
    @Input() public set holidaysDaysOff(value: HolidayModel[]) {
        this._holidaysDaysOff = value;
        this.holidaysAndDaysOffByYear = value.reduce((acc, holiday) => {
            const year = holiday.date.getFullYear();
            let yearGroup = acc.find((group) => group.year === year);
            if (!yearGroup) {
                yearGroup = { year: year, holidays: [] };
                acc.push(yearGroup);
            }
            yearGroup.holidays.push(holiday);
            return acc;
        }, [] as HolidayListModel[]);
        this.filteredList = this.holidaysAndDaysOffByYear;
        this.isLoaded = true;
    }

    @Output() public isDirtyChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    @ViewChild("addDialog") addDialogTemplate: TemplateRef<any>;

    public isLoaded: boolean = false;

    public holidaysAndDaysOffByYear: HolidayListModel[] = [];
    public filteredList: HolidayListModel[] = [];
    public searchValue: string = "";
    public editingDay: HolidayModel | null = null;
    public dialogDayName: string = "";
    public dialogDayDate: string = "";

    updateList() {
        if (!this.searchValue) {
            this.filteredList = this.holidaysAndDaysOffByYear;
            return;
        }
        const searchLower = this.searchValue.toLowerCase();
        this.filteredList = this.holidaysAndDaysOffByYear
            .map((group) => {
                const filteredHolidays = group.holidays.filter((holiday) =>
                    holiday.name.toLowerCase().includes(searchLower)
                );
                return { year: group.year, holidays: filteredHolidays };
            })
            .filter((group) => group.holidays.length > 0);
    }

    openAddHolidayDialog() {
        this.openDialog();
    }

    openEditHolidayDialog(holiday: HolidayModel) {
        this.editingDay = holiday;
        this.dialogDayName = holiday.name;
        this.dialogDayDate = this.getDateString(holiday.date);
        this.openDialog();
    }

    deleteHoliday(holiday: HolidayModel) {
        const year = holiday.date.getFullYear();
        const yearGroup = this.getYearGroup(year);
        if (yearGroup) {
            this.removeHoliday(yearGroup, holiday);
            this.removeYearGroup(yearGroup);
        }
        const dataListIndex = this.holidaysDaysOff.indexOf(holiday);
        if (dataListIndex !== -1) {
            this.holidaysDaysOff.splice(dataListIndex, 1);
        }
        this.isDirtyChange.emit(true);
        this.closeDialog();
    }

    saveHoliday() {
        const holiday: HolidayModel = {
            id: this.editingDay ? this.editingDay.id : null,
            date: new Date(this.dialogDayDate),
            name: this.dialogDayName,
        };
        const year = holiday.date.getFullYear();

        if (this.editingDay) {
            const previousYear = new Date(this.editingDay.date).getFullYear();
            if (previousYear !== year) {
                // Remove from previous year group
                const previousYearGroup = this.getYearGroup(previousYear);
                if (previousYearGroup) {
                    this.removeHoliday(previousYearGroup, this.editingDay);
                    this.removeYearGroup(previousYearGroup);
                }
                // Add to new year group
                const newYearGroup = this.getYearGroup(year);
                this.addHolidayToYearGroup(newYearGroup, holiday);
            } else {
                // Update within the same year group
                const yearGroup = this.getYearGroup(year);
                const index = yearGroup.holidays.indexOf(this.editingDay);
                if (index !== -1) {
                    yearGroup.holidays[index] = holiday;
                    this.sortHolidaysInYearGroup(yearGroup);
                }
            }
            // Update in the overall list
            const dataListIndex = this.holidaysDaysOff.indexOf(this.editingDay);
            if (dataListIndex !== -1) {
                this.holidaysDaysOff[dataListIndex] = holiday;
            }
        } else {
            let yearGroup = this.getYearGroup(year);
            this.addHolidayToYearGroup(yearGroup, holiday);
            this.sortHolidaysInYearGroup(yearGroup);
            this.holidaysDaysOff.push(holiday);
        }
        this.isDirtyChange.emit(true);

        this.closeDialog();
    }

    openDialog() {
        this.dialog
            .open(this.addDialogTemplate)
            .afterClosed()
            .subscribe(() => {
                this.dialogDayName = "";
                this.dialogDayDate = "";
                if (this.editingDay) {
                    this.editingDay = null;
                }
            });
    }

    closeDialog() {
        this.dialog.closeAll();
    }

    private getYearGroup(year: number): HolidayListModel {
        let yearGroup = this.holidaysAndDaysOffByYear.find((group) => group.year === year);
        if (!yearGroup) {
            yearGroup = this.createYearGroup(year);
        }
        return yearGroup;
    }

    private createYearGroup(year: number): HolidayListModel {
        const yearGroup = { year: year, holidays: [] };
        this.holidaysAndDaysOffByYear.push(yearGroup);
        this.sortYearGroups();
        return yearGroup;
    }

    private addHolidayToYearGroup(yearGroup: HolidayListModel, holiday: HolidayModel) {
        yearGroup.holidays.push(holiday);
        this.sortHolidaysInYearGroup(yearGroup);
    }

    private removeHoliday(yearGroup: HolidayListModel, holiday: HolidayModel) {
        const index = yearGroup.holidays.indexOf(holiday);
        if (index !== -1) {
            yearGroup.holidays.splice(index, 1);
        }
    }

    private removeYearGroup(yearGroup: HolidayListModel) {
        if (yearGroup.holidays.length === 0) {
            const prevIndex = this.holidaysAndDaysOffByYear.indexOf(yearGroup);
            if (prevIndex !== -1) {
                this.holidaysAndDaysOffByYear.splice(prevIndex, 1);
            }
        }
    }

    private sortHolidaysInYearGroup(yearGroup: HolidayListModel) {
        yearGroup.holidays.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    }

    private sortYearGroups() {
        this.holidaysAndDaysOffByYear.sort((a, b) => a.year - b.year);
    }

    private getDateString(date: Date): string {
        const offset = date.getTimezoneOffset();
        date = new Date(date.getTime() - offset * 60 * 1000);
        return date.toISOString().split("T")[0];
    }
}
